<script lang="ts">
  import Input from '$shared/atoms/Input/Input.svelte';
  import InputGroup from '$shared/atoms/InputGroup/InputGroup.svelte';
  import AutoComplete from '$shared/atoms/AutoComplete/AutoComplete.svelte';
  import Dropdown from '$shared/atoms/Dropdown/Dropdown.svelte';
  import AddressType, {
    getAddressTypeAsList,
    MapAddressTypeLabel
  } from '$provider/lib/apis/models/AddressType';
  import type { IAddress } from '$provider/lib/apis/interfaces/IResponse';
  import { AddressInputFormDataModel } from '$shared/models/forms/AddressInputFormDataModel';
  import { onMount } from 'svelte';

  interface Props {
    formData?: AddressInputFormDataModel;
    countryList?: CommonList[];
    label: string | undefined;
    onchange?: (formData: AddressInputFormDataModel) => void;
  }

  let { formData = $bindable({}), countryList = [], label, onchange }: Props = $props();

  export const set = (address: IAddress): void => {
    prepareFrom(address);
  };
  // export const validate = async (): Promise<boolean> {

  // }
  let selectedState = '';
  let stateList = AddressInputFormDataModel.getStatesAsList();

  onMount(() => {
    !formData && (formData = new AddressInputFormDataModel());
  });

  const prepareFrom = (address: IAddress): void => {
    if (!address) {
      return;
    }
    formData.addressLine1 = address.addressLine1 || '';
    formData.addressLine2 = address.addressLine2 || '';
    formData.city = address.city || '';
    formData.state = address.state || ''; // state should be id of state from stateList
    formData.zipCode = address.zipCode || '';
    if (formData.state) {
      const tempSelectedState = stateList.find((state) => state?.id === formData.state);
      selectedState = tempSelectedState?.label || '';
    }
    address.type && (formData.type = address.type);
  };

  const dispatchChange = (): void => {
    onchange?.(formData);
  };

  const onChangeAddressLine1 = (detail: any): void => {
    const value = detail.value?.trim();
    formData.setAddressLine1(value);
    dispatchChange();
  };

  const onChangeAddressLine2 = (detail: any): void => {
    const value = detail.value?.trim();
    formData.setAddressLine2(value);
    dispatchChange();
  };

  const onChangeCity = (detail: any): void => {
    const value = detail.value?.trim();
    formData.setCity(value);
    dispatchChange();
  };

  const onStateUpdate = (detail: CommonList): void => {
    formData.setState(detail);
    dispatchChange();
  };

  const onStateCleared = (): void => {
    if (!formData.state) return;
    formData.clearState();
    dispatchChange();
  };

  const onChangeZipCode = (detail: any): void => {
    const value = detail.value?.trim();
    formData.setZipCode(value);
    dispatchChange();
  };

  const onAddressTypeChange = (item: CommonList): void => {
    formData.setType(item.id as AddressType);
    dispatchChange();
  };

  const onCountryUpdate = (detail: CommonList): void => {
    formData.setCountry(detail);
    dispatchChange();
  };

  const onCountryCleared = (): void => {
    if (!formData.countryId) return;
    formData.clearCountry();
    dispatchChange();
  };
</script>

<div class="comp-address-input">
  <!-- Address Line 1 -->
  <div class="input-wrap">
    <Input
      required={false}
      name="addressLine1"
      placeholder="Address Line 1"
      label={label || 'Address'}
      value={formData?.addressLine1 || ''}
      error={formData?.errors?.addressLine1 || ''}
      oninput={onChangeAddressLine1}
    />
  </div>
  <!-- /Address Line 1 -->

  <!-- Address Line 2 -->
  <div class="input-wrap">
    <Input
      required={false}
      name="addressLine2"
      placeholder="Address Line 2 (Optional)"
      value={formData?.addressLine2 || ''}
      oninput={onChangeAddressLine2}
    />
  </div>
  <!-- /Address Line 2 -->

  <InputGroup>
    <!-- City -->
    <div class="input-wrap">
      <Input
        required={false}
        placeholder="City"
        name="city"
        value={formData?.city || ''}
        error={formData?.errors?.city || ''}
        oninput={onChangeCity}
      />
    </div>
    <!-- /City -->

    <!-- State -->
    <div class="input-wrap">
      <AutoComplete
        value={formData?.stateLabel || ''}
        required={false}
        placeholder="State"
        name="state"
        list={stateList}
        error={formData?.errors?.state || ''}
        onclick={onStateUpdate}
        oncleared={onStateCleared}
      />
    </div>
    <!-- /State -->
  </InputGroup>

  <InputGroup>
    <!-- Zip Code -->
    <div class="input-wrap">
      <Input
        required={false}
        placeholder="Zip Code"
        name="zipCode"
        value={formData?.zipCode || ''}
        error={formData?.errors?.zipCode || ''}
        oninput={onChangeZipCode}
      />
    </div>
    <!-- Zip Code -->

    <!-- Country -->
    {#if formData?.showCountryInput}
      <div class="input-wrap">
        <AutoComplete
          value={formData?.countryLabel || ''}
          required={false}
          placeholder="Country"
          name="country"
          list={countryList}
          error={formData?.errors?.countryId || ''}
          onclick={onCountryUpdate}
          oncleared={onCountryCleared}
        />
      </div>
    {/if}
    <!-- Country -->
  </InputGroup>

  <!-- Address Type  -->
  <InputGroup>
    {#if formData?.showTypeInput}
      <div class="input-wrap">
        <div class="input-label">Address Type</div>
        <Dropdown
          islarge
          name="addressType"
          label={MapAddressTypeLabel[formData.type || AddressType.Work]}
          maxWidth={true}
          list={getAddressTypeAsList()}
          onchange={onAddressTypeChange}
        />
      </div>
    {/if}
  </InputGroup>
  <!-- Address Type  -->
</div>

<style>
  .input-wrap {
    margin-bottom: 24px;
  }
  .input-label {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: var(--black);
    margin-bottom: 4px;
  }
</style>
